import React, { memo, useContext, useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "lodash";
import TableCardv2 from "../../../../../components/TableCardv2";
import BasicSelect from "../../../../../components/BasicSelect";
import SnackBar from "../../../../../components/SnackBar";
import { Autocomplete, Checkbox, TextField, Chip, IconButton, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import styles from "./styles.module.css";
import { PreviaInssContext } from "../../../PreviaInssContext";

const STORAGE_PAGE_KEY = "ajustes_verbas_pagina";

const AjustesVerbas = memo(() => {
  const [loading, setLoading] = useState(false);
  const [dataTable, setDataTable] = useState([]);
  const [campoPesquisa, setCampoPesquisa] = useState("Código da rúbrica");
  const [paginaAtual, setPaginaAtual] = useState(() => {
    const p = Number(sessionStorage.getItem(STORAGE_PAGE_KEY));
    return Number.isInteger(p) && p > 0 ? p : 1;
  });

  // filtros
  const [tipoLabel, setTipoLabel] = useState("Todos");
  const [incidenciaLabel, setIncidenciaLabel] = useState("Todas");
  const [compoeLabel, setCompoeLabel] = useState("Todos");
  const [searchTerm, setSearchTerm] = useState("");

  // multiseletor de incidência (mantido conforme sua implementação)
  const descrIncidOptions = ["Mensal", "Não é base de cálculo", "13º salário"];
  const [selectedDescrIncid, setSelectedDescrIncid] = useState([]);
  const [incidOpen, setIncidOpen] = useState(false);
  const incidAutocompleteRef = useRef(null);

  const { dados, setDados, handleAtualizarVersao, loadingVersao } = useContext(PreviaInssContext);

  // helper to show not-editable message
  const showNotEditableMessage = () => {
    setObjSnackBar({ type: "error", msg: "Esta versão não permite alterações." });
  };

  // ---- refs p/ consistência e controle de persistência ----
  const dadosRef = useRef(dados);
  useEffect(() => {
    dadosRef.current = dados;
  }, [dados]);

  // Fechar dropdown de incidência ao clicar fora ou em outro filtro
  useEffect(() => {
    if (!incidOpen) return;

    const handleClickOutside = (event) => {
      const autocompleteElement = incidAutocompleteRef.current;
      if (!autocompleteElement) return;

      const target = event.target;
      
      // Verifica se o clique foi dentro do container do Autocomplete de incidência
      const isClickInIncidContainer = autocompleteElement.contains(target);
      
      // Verifica se o clique foi no popup do MUI Autocomplete (portal)
      const popper = document.querySelector('[data-popper-placement]');
      const isClickInPopper = popper && popper.contains(target);
      
      // Verifica se o clique foi em outro Autocomplete (outro filtro)
      const clickedAutocomplete = target.closest('.MuiAutocomplete-root');
      const isOtherAutocomplete = clickedAutocomplete && 
        !autocompleteElement.querySelector('.MuiAutocomplete-root')?.contains(target);

      // Fecha se clicou fora OU em outro Autocomplete
      if (!isClickInIncidContainer && !isClickInPopper || isOtherAutocomplete) {
        setIncidOpen(false);
      }
    };

    // Adiciona listener com pequeno delay para não fechar imediatamente ao abrir
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside, true);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [incidOpen]);

  // fila/serialização de saves com rollback otimista por item
  const saveInFlightRef = useRef(false);
  const queuedStateRef = useRef(null);
  const lastSentRef = useRef(null);
  const rollbackMapRef = useRef(new Map()); // Map<index_key, estadoAnterior> para rollback individual
  const saveTimeoutRef = useRef(null); // Timeout para debounce de saves
  const paginaAtualRef = useRef(paginaAtual); // Ref para manter página atual ao atualizar dados

  // Sincroniza ref com estado da página
  useEffect(() => {
    paginaAtualRef.current = paginaAtual;
  }, [paginaAtual]);

  const flushSave = async () => {
    if (saveInFlightRef.current) return;

    // Limpa timeout pendente
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    saveInFlightRef.current = true;
    const snapshot = dadosRef.current;
    const previousState = queuedStateRef.current || snapshot;
    lastSentRef.current = snapshot;

    try {
      await handleAtualizarVersao(snapshot);
      // Sucesso: limpa todos os rollbacks
      rollbackMapRef.current.clear();
    } catch (e) {
      // Erro: reverte para o estado anterior
      if (previousState && previousState !== snapshot) {
        const paginaAntesRollback = paginaAtualRef.current || paginaAtual;
        dadosRef.current = previousState;
        setDados(previousState);
        // Re-renderiza tabela com estado revertido, mantendo página atual
        const base = applyFiltersAndSearch(previousState.verbasAjustadas || [], searchTerm, campoPesquisa);
        setDataTable(handleOrganizeData(base));
        // CRÍTICO: Restaura página atual após rollback
        if (paginaAntesRollback && paginaAntesRollback !== paginaAtual) {
          setPaginaAtual(paginaAntesRollback);
          paginaAtualRef.current = paginaAntesRollback;
        }
        setObjSnackBar({ type: "error", msg: "Erro ao salvar alteração. Alteração revertida." });
      } else {
        setObjSnackBar({ type: "error", msg: "Erro ao salvar alteração." });
      }
      rollbackMapRef.current.clear();
    } finally {
      saveInFlightRef.current = false;
      // se houve estado enfileirado diferente do último enviado, dispara novamente
      if (queuedStateRef.current && queuedStateRef.current !== lastSentRef.current) {
        const nextState = queuedStateRef.current;
        queuedStateRef.current = null;
        dadosRef.current = nextState;
        flushSave();
      } else {
        queuedStateRef.current = null;
      }
    }
  };

  const scheduleSave = () => {
    // Cancela o timeout anterior se existir (debounce)
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    
    if (saveInFlightRef.current) {
      // grava o "último desejado" enquanto há um em voo
      queuedStateRef.current = dadosRef.current;
    } else {
      // Debounce de 300ms para agrupar múltiplos cliques rápidos em uma única requisição
      saveTimeoutRef.current = setTimeout(() => {
        saveTimeoutRef.current = null;
        flushSave();
      }, 300);
    }
  };

  const columns = [
    { key: "inicioValidade", label: "Início da validade", width: "120px" },
    { key: "fimValidade", label: "Fim (calculado) da validade", width: "150px" },
    { key: "codigoRubrica", label: "Código da Rúbrica", width: "140px" },
    { key: "descricaoRubrica", label: "Descrição", width: "250px" },
    { key: "tipoRubrica", label: "Tipo da Rúbrica", width: "120px" },
    { key: "descricaoIncidencia", label: "Descrição da incidência", width: "180px" },
    { key: "tagIncidencia", label: "Incidência", width: "120px" },
    { key: "tagComposicaoProjeto", label: "Composição do Projeto", width: "180px" },
  ];

  const valuesIncidencia = ["Sim", "Não", "Nunca"];
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });

  // ---- chaves/índices estáveis ----
  const makeKey = (item) => {
    const code = String(item?.codigoRubrica ?? item?.cod ?? item?.codigo ?? "").trim();
    const inicio = String(item?.inicioValidade ?? item?.inicio ?? "") || "";
    const fim = String(item?.fimValidade ?? item?.fim ?? "") || "";
    return `${code}|${inicio}|${fim}`;
  };

  const getMasterIndex = (row, masterList) => {
    const k = makeKey(row);
    let idx = masterList.findIndex((v) => makeKey(v) === k);
    if (idx !== -1) return idx;

    idx = masterList.findIndex(
      (v) =>
        String(v?.codigoRubrica) === String(row?.codigoRubrica) &&
        String(v?.inicioValidade ?? "") === String(row?.inicioValidade ?? "")
    );
    if (idx !== -1) return idx;

    idx = masterList.findIndex((v) => String(v?.codigoRubrica) === String(row?.codigoRubrica));
    return idx;
  };

  // ---- filtros ----
  const inferTipoRubrica = (item) => {
    const t = String(item?.tipoRubrica ?? item?.tprubr ?? item?.tipo_rubrica ?? item?.tipo ?? "").toUpperCase();
    if (["1", "3", "P"].includes(t)) return "provento";
    if (["2", "4", "D"].includes(t)) return "desconto";
    const txt = String(item?.tipoRubrica ?? item?.descricaoTipo ?? item?.descricao ?? "").toLowerCase();
    if (txt.includes("provento")) return "provento";
    if (txt.includes("desconto")) return "desconto";
    return "desconhecido";
  };

  const getDescricaoIncidencia = (item) => {
    const raw =
      item?.descricaoIncidencia ??
      item?.descricao_codinccp ??
      item?.incidenciaDescricao ??
      item?.categoriaCodinccp ??
      item?.incidencia ??
      "";
    const normalized = String(raw).trim().toLowerCase();

    if (!normalized) return "";
    if (
      normalized === "sim" ||
      normalized === "s" ||
      normalized.includes("mensal")
    ) {
      return "Mensal";
    }
    if (
      normalized === "não" ||
      normalized === "nao" ||
      normalized === "n" ||
      (normalized.includes("não") && normalized.includes("base")) ||
      (normalized.includes("nao") && normalized.includes("base"))
    ) {
      return "Não é base de cálculo";
    }
    if (
      normalized === "nunca" ||
      normalized.includes("13") ||
      normalized.includes("décimo") ||
      normalized.includes("decimo")
    ) {
      return "13º salário";
    }
    return String(raw);
  };

  const matchesCategoriaIncidencia = (item, filterKey) => {
    const incVal = getDescricaoIncidencia(item);
    if (typeof incVal === "number") {
      if (filterKey === "mensal") return incVal === 1;
      if (filterKey === "nao_base_calculo") return item?.isBaseCalculo === false || item?.ehBaseCalculo === false;
      return true;
    }
    const incStr = String(incVal).toLowerCase();
    if (filterKey === "mensal") return incStr.includes("sim") || incStr.includes("mensal");
    if (filterKey === "nao_base_calculo") {
      if ((incStr.includes("não") || incStr.includes("nao")) && incStr.includes("base")) return true;
      if (incStr === "não" || incStr === "nao" || incStr === "n") return true;
      if (item?.isBaseCalculo === false) return true;
      return false;
    }
    if (filterKey === "13_salario") {
      if (incStr.includes("13") || incStr.includes("13º") || incStr.includes("decimo") || incStr.includes("décimo")) return true;
      return false;
    }
    return true;
  };

  const matchesComposicaoProjeto = (item, expected) => {
    const comp = !!item?.composicaoProjeto || String(item?.composicaoProjeto) === "true";
    if (expected === "true") return comp === true;
    if (expected === "false") return comp === false;
    return true;
  };

  const applyFiltersAndSearch = (list = [], term = "", coluna = campoPesquisa) => {
    const tipoFiltro = tipoLabel.toLowerCase();
    // se o multiseletor estiver ativo, usa-o (OR semantics). Caso contrário, usa o filtro único incidenciaLabel
    const useMulti = Array.isArray(selectedDescrIncid) && selectedDescrIncid.length > 0;
    const incFiltro = useMulti
      ? null
      : incidenciaLabel === "Mensal"
      ? "mensal"
      : incidenciaLabel === "Não é base de cálculo"
      ? "nao_base_calculo"
      : "todos";
    const compFiltro = compoeLabel === "Sim" ? "true" : compoeLabel === "Não" ? "false" : "todos";

    let filtered = list.filter((item) => {
      if (tipoFiltro !== "todos") {
        const tipoItem = inferTipoRubrica(item);
        if (tipoFiltro === "provento" && tipoItem !== "provento") return false;
        if (tipoFiltro === "desconto" && tipoItem !== "desconto") return false;
      }

      // filtro de incidência: se multiseleção ativa, será aplicada depois (OR)
      if (!useMulti) {
        if (incFiltro !== "todos") {
          if (!matchesCategoriaIncidencia(item, incFiltro)) return false;
        }
      }

      if (compFiltro !== "todos") {
        if (!matchesComposicaoProjeto(item, compFiltro)) return false;
      }

      return true;
    });

    // aplica multiseleção (OR) se houver opções selecionadas
    if (useMulti) {
      const mapLabelToKey = (label) => {
        if (label === "Mensal") return "mensal";
        if (label === "Não é base de cálculo") return "nao_base_calculo";
        if (label === "13º salário") return "13_salario";
        return null;
      };
      const selectedKeys = selectedDescrIncid.map(mapLabelToKey).filter(Boolean);
      if (selectedKeys.length > 0) {
        filtered = filtered.filter((item) => selectedKeys.some((k) => matchesCategoriaIncidencia(item, k)));
      }
    }

    if (term) {
      const searchValue = String(term).toLowerCase().trim();
      filtered = filtered.filter((item) => {
        if (coluna === "Código da rúbrica") {
          const codigoFonte = String(item.codigoRubrica ?? item.cod ?? item.codigo ?? "");
          return codigoFonte.toLowerCase().includes(searchValue);
        }
        const descFonte = String(item.descricaoRubrica ?? item.descricao ?? "");
        return descFonte.toLowerCase().includes(searchValue);
      });
    }

    return filtered;
  };

  // ---- linhas
  const handleOrganizeData = (data) => {
    // SEMPRE lê do ref atual para garantir valor mais recente e instantâneo
    const master = dadosRef.current?.verbasAjustadas || [];
    return data.map((item) => {
      const masterIndex = getMasterIndex(item, master);
      // Lê diretamente do ref para garantir valor mais recente
      const checked = !!master[masterIndex]?.composicaoProjeto;
      const incidenciaVal = master[masterIndex]?.incidencia;

      return {
        ...item,
        descricaoIncidencia: getDescricaoIncidencia(item),
        tagComposicaoProjeto: (
          <Checkbox
            onChange={(e) => {
              if (!dados.isEditable) {
                showNotEditableMessage();
                return;
              }
              handleChangeRow(masterIndex, "composicaoProjeto", e.target.checked);
            }}
            checked={checked}
            disabled={!dados.isEditable}
          />
        ),
        tagIncidencia: (
          <BasicSelect
            options={valuesIncidencia}
            onChange={(e) => {
              if (!dados.isEditable) {
                showNotEditableMessage();
                return;
              }
              handleChangeRow(masterIndex, "incidencia", e.target.value);
            }}
            value={incidenciaVal}
            disabled={!dados.isEditable}
          />
        ),
        style: checked ? {} : { color: "red" },
      };
    });
  };

  // ---- efeitos
  useEffect(() => {
    if ((dados.verbasAjustadas || []).length > 0) {
      const base = applyFiltersAndSearch(dados.verbasAjustadas, searchTerm, campoPesquisa);
      setDataTable(handleOrganizeData(base));
    } else {
      setDataTable([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dados.verbasAjustadas, selectedDescrIncid]);

  useEffect(() => {
    const base = applyFiltersAndSearch(dados.verbasAjustadas || [], searchTerm, campoPesquisa);
    setDataTable(handleOrganizeData(base));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoLabel, incidenciaLabel, compoeLabel, campoPesquisa, selectedDescrIncid, searchTerm]);

  const handleDebounce = (value) => {
    setLoading(true);
    setSearchTerm(value || "");
    const base = applyFiltersAndSearch(dadosRef.current.verbasAjustadas || [], value || "", campoPesquisa);
    setDataTable(handleOrganizeData(base));
    setLoading(false);
  };
  const functionDebounce = useMemo(
    () => debounce((value) => handleDebounce(value), 300),
    [campoPesquisa]
  );

  useEffect(() => {
    return () => {
      functionDebounce.cancel();
    };
  }, [functionDebounce]);

  // ---- mutação + persistência serializada com atualização otimista instantânea
  const handleChangeRow = (index, key, value) => {
    if (!dados.isEditable) {
      setObjSnackBar({ type: "error", msg: "Esta versão não permite alterações." });
      return;
    }

    const master = [...(dadosRef.current.verbasAjustadas || [])];
    if (!master[index]) {
      setObjSnackBar({ type: "error", msg: "Erro interno: verba não localizada." });
      return;
    }

    // Guarda estado anterior apenas do item específico para rollback individual
    const itemKey = `${index}_${key}`;
    const previousItemValue = master[index][key];
    rollbackMapRef.current.set(itemKey, { index, key, value: previousItemValue });

    // ATUALIZAÇÃO OTIMISTA INSTANTÂNEA: atualiza imediatamente sem esperar a requisição
    const updatedItem = { ...master[index], [key]: value };
    master[index] = updatedItem;

    // espelha no resumo (não remove linhas)
    const updatedResumo = (dadosRef.current.resumo || []).map((item) => {
      const matchCodigo = String(item.codigoRubricaCompleto) === String(updatedItem.codigoRubrica);
      return matchCodigo ? { ...item, composicaoProjeto: updatedItem.composicaoProjeto } : item;
    });

    const updatedData = {
      ...dadosRef.current,
      verbasAjustadas: master,
      resumo: updatedResumo,
    };

    // ATUALIZAÇÃO OTIMISTA: atualiza imediatamente sem esperar a requisição
    // CRÍTICO: Guarda página atual ANTES de atualizar dados para não perder
    const paginaAtualAntesUpdate = paginaAtualRef.current || paginaAtual;
    
    dadosRef.current = updatedData;
    setDados(updatedData);

    // Re-renderiza tabela imediatamente com novo estado
    const base = applyFiltersAndSearch(master, searchTerm, campoPesquisa);
    setDataTable(handleOrganizeData(base));
    
    // CRÍTICO: Mantém página atual ao editar (evita reset para página 1)
    // Força atualização da página para garantir que não seja resetada pelo TableCardv2
    if (paginaAtualAntesUpdate && paginaAtualAntesUpdate !== paginaAtual) {
      setPaginaAtual(paginaAtualAntesUpdate);
    }
    paginaAtualRef.current = paginaAtualAntesUpdate;
    sessionStorage.setItem(STORAGE_PAGE_KEY, String(paginaAtualAntesUpdate));

    // agenda persistência em background (se falhar, rollback será feito no flushSave)
    scheduleSave();
  };

  // Estado para controlar qual filtro está em foco/hover (apenas para feedback visual nos filtros, não ofusca)
  const [filtroFocado, setFiltroFocado] = useState(null);
  const [tipoOpen, setTipoOpen] = useState(false);
  const [compoeOpen, setCompoeOpen] = useState(false);
  const [colunaOpen, setColunaOpen] = useState(false);

  // ---- UI - Estilos para Select (FormControl + InputLabel)
  const styleFormControl = useMemo(() => ({
    minWidth: 180,
    width: 220,
  }), []);

  // ---- UI - Estilos para Autocomplete com label flutuante
  const styleAutoComplete = useMemo(() => ({
    minWidth: 180,
    width: 220,
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      padding: "2px 5px",
      "& fieldset": { borderColor: "#18316e" },
      "&:hover fieldset": { borderColor: "#18316e" },
      "&.Mui-focused fieldset": { borderColor: "#18316e", maxHeight: "5rem", overflow: "scroll" },
    },
    // garante contexto para position: 'absolute' do container de chips
    position: "relative",
  }), []);

  // Memoiza o componente Complemento para evitar re-renderizações que causam "pulo" na tela
  const Complemento = useMemo(() => {
    return (
      <div className={styles.containerHeader}>
        {/* ESQUERDA: filtros organizados na ordem das colunas da tabela */}
        <div className={styles.filtrosEsquerda}>
          {/* 1. Código/Descrição da Rúbrica (Coluna de pesquisa) - corresponde às colunas 3 e 4 */}
          <div 
            className={styles.filtroItem}
            onMouseEnter={() => !colunaOpen && setFiltroFocado("coluna")}
            onMouseLeave={() => !colunaOpen && setFiltroFocado(null)}
          >
            <FormControl size="small" sx={styleFormControl}>
              <InputLabel>Coluna de pesquisa</InputLabel>
              <Select
                value={campoPesquisa}
                label="Coluna de pesquisa"
                onChange={(e) => {
                  setCampoPesquisa(e.target.value);
                  setIncidOpen(false);
                }}
                onOpen={() => {
                  setIncidOpen(false);
                  setColunaOpen(true);
                  setFiltroFocado("coluna");
                }}
                onClose={() => {
                  setColunaOpen(false);
                  setFiltroFocado(null);
                }}
              >
                <MenuItem value="Código da rúbrica">Código da rúbrica</MenuItem>
                <MenuItem value="Descrição da rúbrica">Descrição da rúbrica</MenuItem>
              </Select>
            </FormControl>
          </div>
          
          {/* 2. Tipo da Rúbrica - corresponde à coluna 5 */}
          <div 
            className={styles.filtroItem}
            onMouseEnter={() => !tipoOpen && setFiltroFocado("tipo")}
            onMouseLeave={() => !tipoOpen && setFiltroFocado(null)}
          >
            <FormControl size="small" sx={styleFormControl}>
              <InputLabel>Tipo da Rúbrica</InputLabel>
              <Select
                value={tipoLabel}
                label="Tipo da Rúbrica"
                onChange={(e) => {
                  setTipoLabel(e.target.value);
                  setIncidOpen(false);
                }}
                onOpen={() => {
                  setIncidOpen(false);
                  setTipoOpen(true);
                  setFiltroFocado("tipo");
                }}
                onClose={() => {
                  setTipoOpen(false);
                  setFiltroFocado(null);
                }}
              >
                <MenuItem value="Todos">Todos</MenuItem>
                <MenuItem value="Provento">Provento</MenuItem>
                <MenuItem value="Desconto">Desconto</MenuItem>
              </Select>
            </FormControl>
          </div>
          
          {/* 3. Descrição da incidência - corresponde à coluna 6 */}
          <div 
            ref={incidAutocompleteRef} 
            className={styles.filtroItem} 
            style={{ 
              position: "relative", 
              minWidth: 200,
            }}
            onMouseEnter={() => setFiltroFocado("incidencia")}
            onMouseLeave={() => !incidOpen && setFiltroFocado(null)}
          >
            <FormControl size="small" sx={styleFormControl}>
              <InputLabel 
                shrink={selectedDescrIncid.length > 0 || incidOpen}
                sx={{ 
                  backgroundColor: "white", 
                  paddingLeft: "4px", 
                  paddingRight: "4px",
                  transform: (selectedDescrIncid.length > 0 || incidOpen) ? "translate(14px, -9px) scale(0.75)" : "translate(14px, 9px) scale(1)",
                  transition: "transform 0.2s ease"
                }}
              >
                Descrição da incidência
              </InputLabel>
              <Autocomplete
                multiple
                disableCloseOnSelect
                open={incidOpen}
                onOpen={() => {
                  setIncidOpen(true);
                  setFiltroFocado("incidencia");
                }}
                onClose={(_, reason) => {
                  if (reason !== "selectOption") {
                    setIncidOpen(false);
                    setFiltroFocado(null);
                  }
                }}
                value={selectedDescrIncid}
                sx={{
                  ...styleAutoComplete,
                  "& .MuiOutlinedInput-root": {
                    ...styleAutoComplete["& .MuiOutlinedInput-root"],
                    paddingTop: selectedDescrIncid.length > 0 ? "8px" : "2px",
                  },
                }}
                options={descrIncidOptions}
                isOptionEqualToValue={(opt, val) => opt === val}
                onChange={(_, values) => setSelectedDescrIncid(values || [])}
                renderTags={() => null}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox style={{ marginRight: 8 }} checked={selected} tabIndex={-1} disableRipple />
                    {option}
                  </li>
                )}
                renderInput={(params) => {
                  const { InputProps } = params;
                  const { endAdornment, ...otherInputProps } = InputProps;
                  
                  return (
                    <TextField
                      {...params}
                      size="small"
                      variant="outlined"
                      InputProps={{
                        ...otherInputProps,
                        endAdornment: (
                          <>
                            {selectedDescrIncid.length > 0 && (
                              <div style={{ display: "flex", gap: 4, marginRight: 8, alignItems: "center", flexWrap: "wrap", maxWidth: "calc(100% - 50px)" }}>
                                {selectedDescrIncid.slice(0, 2).map((option, index) => (
                                  <Chip key={option} size="small" label={option} style={{ height: "20px", fontSize: "0.7rem" }} />
                                ))}
                                {selectedDescrIncid.length > 2 && (
                                  <Chip size="small" label={`+${selectedDescrIncid.length - 2}`} style={{ height: "20px", fontSize: "0.7rem" }} />
                                )}
                              </div>
                            )}
                            {endAdornment}
                          </>
                        ),
                      }}
                    />
                  );
                }}
              />
            </FormControl>
          </div>
          
          {/* 4. Compõe (Composição do Projeto) - corresponde à coluna 8 */}
          <div 
            className={styles.filtroItem}
            onMouseEnter={() => !compoeOpen && setFiltroFocado("compoe")}
            onMouseLeave={() => !compoeOpen && setFiltroFocado(null)}
          >
            <FormControl size="small" sx={styleFormControl}>
              <InputLabel>Compõe</InputLabel>
              <Select
                value={compoeLabel}
                label="Compõe"
                onChange={(e) => {
                  setCompoeLabel(e.target.value);
                  setIncidOpen(false);
                }}
                onOpen={() => {
                  setIncidOpen(false);
                  setCompoeOpen(true);
                  setFiltroFocado("compoe");
                }}
                onClose={() => {
                  setCompoeOpen(false);
                  setFiltroFocado(null);
                }}
              >
                <MenuItem value="Todos">Todos</MenuItem>
                <MenuItem value="Sim">Sim</MenuItem>
                <MenuItem value="Não">Não</MenuItem>
              </Select>
            </FormControl>
          </div>
        </div>

        {/* DIREITA: vazio (filtros agora estão todos à esquerda na ordem das colunas) */}
        <div className={styles.filtrosDireita}></div>
      </div>
    );
  }, [tipoLabel, compoeLabel, campoPesquisa, selectedDescrIncid, incidOpen, styleAutoComplete, styleFormControl, descrIncidOptions]);

  // Mantém foco enquanto dropdown está aberto
  useEffect(() => {
    if (incidOpen) {
      setFiltroFocado("incidencia");
    }
  }, [incidOpen]);

  // Determina qual coluna corresponde ao filtro em hover (apenas preview visual)
  const colunaHover = useMemo(() => {
    if (!filtroFocado) return null;
    
    switch (filtroFocado) {
      case "tipo":
        return "tipoRubrica";
      case "incidencia":
        return "descricaoIncidencia";
      case "compoe":
        return "tagComposicaoProjeto";
      case "coluna":
        // Depende do campo de pesquisa selecionado
        if (campoPesquisa === "Código da rúbrica") {
          return "codigoRubrica";
        }
        if (campoPesquisa === "Descrição da rúbrica") {
          return "descricaoRubrica";
        }
        return null;
      default:
        return null;
    }
  }, [filtroFocado, campoPesquisa]);

  return (
    <>
      <TableCardv2
        complementaryHeaderTop={Complemento}
        uniqueEmpresa
        columns={columns}
        data={dataTable}
        debounce={functionDebounce}
        loading={loading}
        loadingVersao={loadingVersao}
        // A busca atua sobre a coluna selecionada em "Coluna de pesquisa"
        // por isso o placeholder foi ajustado para refletir o comportamento real
        searchPlaceholder="Buscar na coluna selecionada"
        paginaAtual={paginaAtual}
        colunaHover={colunaHover}
        setPaginaAtual={(p) => {
          setPaginaAtual(p);
          paginaAtualRef.current = p; // Atualiza ref quando usuário muda página manualmente
          sessionStorage.setItem(STORAGE_PAGE_KEY, String(p));
        }}
      />
      <SnackBar objSnackBar={objSnackBar} setObjSnackBar={setObjSnackBar} />
    </>
  );
});

export default AjustesVerbas;
